'use client';

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { PublicShareManifest, SnapshotItem } from '@containd/shared';
import {
  absolutizeUrl,
  canvasOgImageMediaId,
  canvasPageDescription,
  canvasPageTitle,
  shareDocumentTitle,
} from '@containd/shared';
import { missingBakeItems, nextPublishedLayoutIndex, resolveSharePaintColors } from '@/lib/freezePaint';
import {
  buildShareCanvasStack,
  canvasUuidForItem,
  isShareRootItem,
  pathForShareVisit,
  pushShareCanvasVisit,
  resolvePlaceholderOriginal,
  shareLocationEquals,
  sharePathSegments,
  stackFromShareLocation,
  visitsFromAncestry,
  withFocusOnCurrentVisit,
  type ShareVisit,
} from '@/lib/shareNavigation';
import { resolveShareMediaUrl } from '@/lib/shareMediaUrl';
import { AccountMenu } from './AccountMenu';
import { SocialShareBar } from './SocialShareBar';
import { ViewingCanvas } from './ViewingCanvas';
import { ItemViewer } from './ItemViewer';
import {
  LegalFooter,
  reportContentHref,
  viewerMenuLinks,
} from './LegalFooter';
import {
  ShareReportDialog,
  currentIdToken,
} from './ShareReportDialog';
import { ShareBreadcrumbBar } from './ShareBreadcrumbBar';
import { PublicUiProvider, usePublicUi } from '@/lib/publicUi';
import { linkifyHashtags } from '@/lib/linkifyHashtags';
import { useNarrowScreen } from '@/hooks/useNarrowScreen';
import styles from './ShareViewer.module.css';

function shareHeartApiBase(): string {
  if (typeof window !== 'undefined') {
    const w = window as Window & { __PUBLIC_SHARE_API_BASE?: string };
    if (w.__PUBLIC_SHARE_API_BASE) return w.__PUBLIC_SHARE_API_BASE.replace(/\/$/, '');
  }
  return (
    process.env.NEXT_PUBLIC_PUBLIC_SHARE_API_BASE || 'https://api.containd.app/v1'
  ).replace(/\/$/, '');
}

function ShareHeartControl({ shareId }: { shareId: string }) {
  const { extra } = usePublicUi();
  const [heartCount, setHeartCount] = useState(0);
  const [hearted, setHearted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = currentIdToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    void fetch(`${shareHeartApiBase()}/shareHeart?shareId=${encodeURIComponent(shareId)}`, {
      headers,
    })
      .then((r) => (r.ok ? r.json() : { heartCount: 0 }))
      .then((data: { heartCount?: number; hearted?: boolean }) => {
        if (cancelled) return;
        setHeartCount(Math.max(0, Number(data.heartCount) || 0));
        setHearted(data.hearted === true);
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  const toggle = () => {
    const token = currentIdToken();
    if (!token) {
      const loc = (document.documentElement.getAttribute('lang') || 'en').toLowerCase();
      const locale = ['en', 'de', 'jp'].includes(loc) ? loc : 'en';
      const next = encodeURIComponent(window.location.href);
      window.location.href = `https://containd.space/${locale}/login/?next=${next}`;
      return;
    }
    if (busy) return;
    const prevCount = heartCount;
    const prevHearted = hearted;
    setBusy(true);
    setHearted(!prevHearted);
    setHeartCount(prevHearted ? Math.max(0, prevCount - 1) : prevCount + 1);
    void fetch(`${shareHeartApiBase()}/shareHeart`, {
      method: prevHearted ? 'DELETE' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ shareId }),
    })
      .then(async (r) => {
        if (r.status === 401) {
          setHearted(prevHearted);
          setHeartCount(prevCount);
          const loc = (document.documentElement.getAttribute('lang') || 'en').toLowerCase();
          const locale = ['en', 'de', 'jp'].includes(loc) ? loc : 'en';
          const next = encodeURIComponent(window.location.href);
          window.location.href = `https://containd.space/${locale}/login/?next=${next}`;
          return;
        }
        if (!r.ok) {
          setHearted(prevHearted);
          setHeartCount(prevCount);
          return;
        }
        const data = (await r.json()) as { heartCount?: number; hearted?: boolean };
        if (data.heartCount != null) setHeartCount(Math.max(0, Number(data.heartCount) || 0));
        if (data.hearted != null) setHearted(data.hearted);
      })
      .catch(() => {
        setHearted(prevHearted);
        setHeartCount(prevCount);
      })
      .finally(() => setBusy(false));
  };

  return (
    <button
      type="button"
      className={`${styles.shareHeart}${hearted ? ` ${styles.shareHeartOn}` : ''}`}
      aria-label={extra.shareHeart}
      aria-pressed={hearted}
      disabled={busy}
      onClick={toggle}
    >
      <span aria-hidden>♥</span>
      {heartCount > 0 ? (
        <span className={styles.shareHeartN} title={String(heartCount)}>
          {formatCompactCount(heartCount)}
        </span>
      ) : null}
    </button>
  );
}

function formatCompactCount(n: number): string {
  const v = Math.max(0, Math.floor(Number(n) || 0));
  if (v < 1000) return String(v);
  if (v < 10000) {
    return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  return `${Math.round(v / 1000)}k`;
}

function formatViews(
  n: number,
  extra: { shareViewOne: string; shareViewsMany: string },
): string {
  if (n <= 0) return '';
  if (n === 1) return extra.shareViewOne;
  if (n < 1000) return extra.shareViewsMany.replace('{n}', String(n));
  return extra.shareViewsMany.replace('{n}', formatCompactCount(n));
}

function ShareViewCount({ shareId }: { shareId: string }) {
  const { extra } = usePublicUi();
  const [viewCount, setViewCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const token = currentIdToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const base = shareHeartApiBase();
    void fetch(`${base}/contentView?kind=share&id=${encodeURIComponent(shareId)}`, {
      headers,
    })
      .then((r) => (r.ok ? r.json() : { viewCount: 0 }))
      .then(async (data: { viewCount?: number }) => {
        if (cancelled) return;
        setViewCount(Math.max(0, Number(data.viewCount) || 0));
        const key = `containd:viewed:share:${shareId}`;
        try {
          if (sessionStorage.getItem(key)) return;
          sessionStorage.setItem(key, '1');
        } catch {
          /* private mode still POSTs; server daily-dedupes */
        }
        const postHeaders: Record<string, string> = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        };
        if (token) postHeaders.Authorization = `Bearer ${token}`;
        const r = await fetch(`${base}/contentView`, {
          method: 'POST',
          headers: postHeaders,
          body: JSON.stringify({ kind: 'share', id: shareId }),
        });
        if (!r.ok || cancelled) return;
        const next = (await r.json()) as { viewCount?: number };
        if (next.viewCount != null) setViewCount(Math.max(0, Number(next.viewCount) || 0));
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, [shareId]);

  if (viewCount <= 0) return null;
  return (
    <span className={styles.shareViews} title={String(viewCount)}>
      {formatViews(viewCount, extra)}
    </span>
  );
}

interface ShareViewerProps {
  manifest: PublicShareManifest;
  /**
   * Canvas parent to open. Omit / root → share root canvas (inside the list).
   * Outer canvas (parent=null) is never a viewer level.
   */
  initialParentUuid?: string | null;
  initialItemUuid?: string | null;
  /** Third-party embed: chrome-slim (hide site header/nav/social/account). */
  embedded?: boolean;
  /** True when this Space may be embedded (shows the Embed share option). */
  embeddable?: boolean;
}

function ownerLabel(manifest: PublicShareManifest): string {
  return (
    manifest.owner.displayName?.trim() ||
    manifest.owner.alias?.trim() ||
    'Owner'
  );
}

function catalogTemplateLinks(
  manifest: PublicShareManifest,
  locale: string,
): { href: string; label: string }[] {
  const catalogHost = (
    process.env.NEXT_PUBLIC_CATALOG_HOST || 'https://catalog.containd.app'
  ).replace(/\/$/, '');
  const loc = locale || 'en';
  const refs = (
    Array.isArray(manifest.catalogRefs) ? manifest.catalogRefs : []
  ).filter(
    (r) =>
      r &&
      typeof r.catalogSlug === 'string' &&
      r.catalogSlug.trim() &&
      typeof r.templateId === 'string' &&
      r.templateId !== 'default',
  );
  if (refs.length > 0) {
    return refs.slice(0, 4).map((r) => ({
      href: `${catalogHost}/${loc}/t/${encodeURIComponent(r.catalogSlug)}/`,
      label: r.title || r.catalogSlug,
    }));
  }
  const fallbackIds = [
    ...new Set(
      manifest.items
        .map((i) => i.templateId)
        .filter((id) => id && id !== 'default'),
    ),
  ].slice(0, 4);
  return fallbackIds.map((id) => {
    const slugOk =
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/i.test(id) &&
      !/^tmpl-/i.test(id) &&
      !/^template-/i.test(id);
    return {
      href: slugOk
        ? `${catalogHost}/${loc}/t/${encodeURIComponent(id)}/`
        : `${catalogHost}/${loc}/?q=${encodeURIComponent(id)}`,
      label: id,
    };
  });
}

export function ShareViewer({
  manifest,
  initialParentUuid,
  initialItemUuid = null,
  embedded = false,
  embeddable = false,
}: ShareViewerProps) {
  return (
    <PublicUiProvider locale={manifest.locale}>
      <ShareViewerInner
        manifest={manifest}
        initialParentUuid={initialParentUuid}
        initialItemUuid={initialItemUuid}
        embedded={embedded}
        embeddable={embeddable}
      />
    </PublicUiProvider>
  );
}

function ShareViewerInner({
  manifest,
  initialParentUuid,
  initialItemUuid = null,
  embedded = false,
  embeddable = false,
}: ShareViewerProps) {
  const { locale, extra, lists } = usePublicUi();
  const narrow = useNarrowScreen();
  const [menuOpen, setMenuOpen] = useState(false);
  const [stack, setStack] = useState<ShareVisit[]>(() => {
    const visits = visitsFromAncestry(
      buildShareCanvasStack(manifest, initialParentUuid),
    );
    if (initialItemUuid && visits.length > 0) {
      const last = visits[visits.length - 1]!;
      visits[visits.length - 1] = {
        ...last,
        focusItemUuid: initialItemUuid,
      };
    }
    return visits;
  });
  const parentUuid =
    stack[stack.length - 1]?.canvasUuid ?? manifest.rootItemUuid;
  const focusItemUuid = stack[stack.length - 1]?.focusItemUuid ?? null;
  const [selected, setSelected] = useState<SnapshotItem | null>(null);
  const [layoutOverride, setLayoutOverride] = useState<Record<string, number>>(
    {},
  );
  const [reportOpen, setReportOpen] = useState(false);
  const [absoluteShareUrl, setAbsoluteShareUrl] = useState(() => {
    if (manifest.visibility === 'public' && manifest.slug) {
      return `/s/${manifest.slug}`;
    }
    if (manifest.token) return `/share/${manifest.token}`;
    return '/';
  });
  const ignoreNextHistoryWrite = useRef(false);
  const prevCanvasForHistory = useRef<string | null>(null);

  const sharePageTitle = canvasPageTitle(manifest, parentUuid);
  const sharePageDescription = canvasPageDescription(manifest, parentUuid);

  const embedCode = useMemo(() => {
    if (!embeddable || !manifest.slug || manifest.visibility !== 'public') {
      return null;
    }
    const url = `https://containd.space/embed/s/${encodeURIComponent(manifest.slug)}/`;
    const title = sharePageTitle.replace(/"/g, '&quot;');
    return `<iframe src="${url}" style="width:100%;aspect-ratio:4/3;border:0" loading="lazy" title="${title}"></iframe>`;
  }, [embeddable, manifest.slug, manifest.visibility, sharePageTitle]);

  useEffect(() => {
    document.title = shareDocumentTitle(sharePageTitle);
  }, [sharePageTitle]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPopState = () => {
      ignoreNextHistoryWrite.current = true;
      setStack(
        stackFromShareLocation(
          manifest,
          window.location.pathname,
          window.location.search,
        ),
      );
      setSelected(null);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [manifest]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const visit = stack[stack.length - 1];
    if (!visit) return;
    const nextPath = pathForShareVisit(manifest, visit);
    const current = `${window.location.pathname}${window.location.search}`;
    const absolute = `${window.location.origin}${nextPath}`;

    if (ignoreNextHistoryWrite.current) {
      ignoreNextHistoryWrite.current = false;
      prevCanvasForHistory.current = visit.canvasUuid;
      setAbsoluteShareUrl(window.location.href.split('#')[0] ?? absolute);
      return;
    }

    if (shareLocationEquals(current, nextPath)) {
      prevCanvasForHistory.current = visit.canvasUuid;
      setAbsoluteShareUrl(window.location.href.split('#')[0] ?? absolute);
      return;
    }

    const sameCanvas = prevCanvasForHistory.current === visit.canvasUuid;
    const landing =
      prevCanvasForHistory.current == null &&
      shareLocationEquals(
        current.replace(/\?.*$/, ''),
        nextPath.replace(/\?.*$/, ''),
      );
    if (sameCanvas || landing || prevCanvasForHistory.current == null) {
      window.history.replaceState(null, '', nextPath);
    } else {
      window.history.pushState(null, '', nextPath);
    }
    prevCanvasForHistory.current = visit.canvasUuid;
    setAbsoluteShareUrl(`${window.location.origin}${nextPath}`.split('#')[0]!);
  }, [stack, manifest]);

  const chunk = useMemo(() => {
    return (
      manifest.canvases.find((c) => c.parentUuid === parentUuid) ?? null
    );
  }, [manifest, parentUuid]);

  const items = useMemo(() => {
    if (!chunk) return [];
    const set = new Set(chunk.itemUuids);
    return manifest.items
      .filter((i) => set.has(i.uuid))
      .sort((a, b) => (a.geometry.zIndex ?? 0) - (b.geometry.zIndex ?? 0));
  }, [manifest, chunk]);

  const ancestry = useMemo(
    () => buildShareCanvasStack(manifest, parentUuid),
    [manifest, parentUuid],
  );
  const pathSegments = useMemo(
    () => sharePathSegments(manifest, ancestry),
    [ancestry, manifest],
  );

  const revealPlaceholderOriginal = (placeholder: SnapshotItem) => {
    const original = resolvePlaceholderOriginal(manifest, placeholder);
    if (!original) return;
    const dest = canvasUuidForItem(manifest, original.uuid);
    if (!dest) return;
    setStack((s) => {
      const current = s[s.length - 1]?.canvasUuid;
      if (current === dest) {
        return pushShareCanvasVisit(s, {
          canvasUuid: dest,
          focusItemUuid: original.uuid,
        });
      }
      const marked = withFocusOnCurrentVisit(s, placeholder.uuid);
      return pushShareCanvasVisit(marked, {
        canvasUuid: dest,
        focusItemUuid: original.uuid,
      });
    });
    setSelected(null);
  };

  const paintColors = useMemo(
    () => resolveSharePaintColors(manifest),
    [manifest],
  );

  const bakeGaps = useMemo(() => missingBakeItems(manifest), [manifest]);

  const svgBackgroundUrl = useMemo(() => {
    // Assembler already copies item svgGraphics → canvas.svgBackgroundMediaId.
    // Do not fall back to parentItem — that paints a broken bg on canvases with none.
    return resolveShareMediaUrl(manifest, chunk?.svgBackgroundMediaId);
  }, [chunk, manifest]);

  const cycleLayout = (item: SnapshotItem, delta: number) => {
    setLayoutOverride((prev) => {
      const cur = prev[item.uuid] ?? item.layoutInUse ?? 0;
      return {
        ...prev,
        [item.uuid]: nextPublishedLayoutIndex(manifest, item, cur, delta),
      };
    });
  };

  const profileHref = manifest.owner.profileUrl ?? null;
  const templateLinks = useMemo(
    () => catalogTemplateLinks(manifest, locale),
    [manifest, locale],
  );

  const shareImageUrl = useMemo(() => {
    const mediaId = canvasOgImageMediaId(manifest, parentUuid);
    const resolved = mediaId ? resolveShareMediaUrl(manifest, mediaId) : null;
    if (!resolved) return null;
    if (typeof window === 'undefined') return resolved;
    return absolutizeUrl(resolved, window.location.origin);
  }, [manifest, parentUuid]);

  const spacesHome =
    manifest.locale
      ? `https://containd.space/${encodeURIComponent(manifest.locale)}/`
      : 'https://containd.space/';

  const reportHref = reportContentHref({
    url: absoluteShareUrl,
    slug: manifest.slug,
  });

  const onReportFromMenu = () => {
    if (!currentIdToken()) return false;
    setReportOpen(true);
    setMenuOpen(false);
    return true;
  };

  const menuLegal = viewerMenuLinks({
    locale,
    lists,
    extra,
    reportHref,
    onReportClick: onReportFromMenu,
  });

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setMenuOpen(false);
    };
    document.body.classList.add('share-nav-open');
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('share-nav-open');
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const ownerBlock: ReactNode = (
    <>
      {embedded ? null : (
        <>
          <ShareHeartControl shareId={manifest.shareId} />
          <ShareViewCount shareId={manifest.shareId} />
        </>
      )}
      {profileHref ? (
        <a
          href={profileHref}
          className={styles.ownerLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          {ownerLabel(manifest)}
        </a>
      ) : (
        <span className={styles.ownerName}>{ownerLabel(manifest)}</span>
      )}
    </>
  );

  const aboutText = (
    manifest.contextDescription ||
    manifest.description ||
    ''
  ).trim();
  const aboutLocale = locale || manifest.locale || 'en';
  const aboutBlock: ReactNode = aboutText ? (
    <div className={styles.infoAbout}>
      <p className={styles.infoAboutText}>
        {linkifyHashtags(aboutText, aboutLocale, styles.infoHashtag)}
      </p>
    </div>
  ) : null;

  /** Desktop: (i) only with description. Mobile: always (owner chrome folds into panel). */
  const showPathInfo = narrow || !!aboutText;

  const catalogNav =
    templateLinks.length > 0 ? (
      <nav
        className={styles.catalogInline}
        aria-label={extra.shareRelatedTemplates}
      >
        <span>{extra.shareTemplatesLabel}</span>
        {templateLinks.map((link) => (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {link.label}
          </a>
        ))}
      </nav>
    ) : null;

  return (
    <div
      className={styles.root}
      data-theme={paintColors.theme}
      style={{
        backgroundColor: paintColors.viewportBackground,
        color: paintColors.text,
        colorScheme: paintColors.theme,
        ['--share-bg' as string]: paintColors.viewportBackground,
        ['--share-surface' as string]: paintColors.surface,
        ['--share-text' as string]: paintColors.text,
        ['--share-primary' as string]: paintColors.primary,
      }}
    >
      {embedded ? null : (
      <header className={styles.siteHeader}>
        <button
          type="button"
          className={styles.menuBtn}
          aria-expanded={menuOpen}
          aria-controls="share-nav-drawer"
          aria-label={extra.shareMenuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <svg viewBox="0 0 24 24" aria-hidden fill="currentColor">
            <path d="M4 6.75A.75.75 0 0 1 4.75 6h14.5a.75.75 0 0 1 0 1.5H4.75A.75.75 0 0 1 4 6.75Zm0 5.25a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H4.75A.75.75 0 0 1 4 12Zm0 5.25a.75.75 0 0 1 .75-.75h14.5a.75.75 0 0 1 0 1.5H4.75a.75.75 0 0 1-.75-.75Z" />
          </svg>
        </button>
        <a href={spacesHome} className={styles.brandLink}>
          <img
            src="https://containd.space/_assets/brand/containdLogoSmall.png"
            alt=""
            width={28}
            height={28}
          />
          <span className={styles.brandText}>{extra.shareContaindSpaces}</span>
        </a>
        <div className={styles.siteHeaderRight}>
          {manifest.visibility === 'public' ? (
            <SocialShareBar
              url={absoluteShareUrl}
              title={sharePageTitle}
              description={sharePageDescription}
              imageUrl={shareImageUrl}
              embedCode={embedded ? null : embedCode}
            />
          ) : null}
          <a
            className={styles.appPromo}
            href="https://www.containd.app"
            target="_blank"
            rel="noopener noreferrer"
          >
            {extra.shareYours} <strong>Containd</strong>
          </a>
          <div className={styles.headerAccount}>
            <AccountMenu />
          </div>
        </div>
      </header>
      )}

      {!embedded && menuOpen ? (
        <div
          className={styles.navDrawer}
          id="share-nav-drawer"
          role="dialog"
          aria-modal="true"
          aria-label={extra.shareMenuOpen}
        >
          <button
            type="button"
            className={styles.navBackdrop}
            aria-label={extra.shareMenuClose}
            onClick={() => setMenuOpen(false)}
          />
          <nav className={styles.navPanel}>
            <div className={styles.navPanelHead}>
              <strong>{extra.shareMenuOpen}</strong>
              <button
                type="button"
                className={styles.navClose}
                aria-label={extra.shareMenuClose}
                onClick={() => setMenuOpen(false)}
              >
                ×
              </button>
            </div>
            <a
              className={styles.drawerLink}
              href={spacesHome}
              onClick={() => setMenuOpen(false)}
            >
              {extra.shareContaindSpaces}
            </a>
            <a
              className={styles.drawerLink}
              href="https://www.containd.app"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
            >
              {extra.shareYours} Containd
            </a>
            {templateLinks.length > 0 ? (
              <div className={styles.drawerSection}>
                <div className={styles.drawerSectionLabel}>
                  {extra.shareRelatedTemplates}
                </div>
                {templateLinks.map((link) => (
                  <a
                    key={link.href}
                    className={styles.drawerLink}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            ) : null}
            <div className={styles.drawerSection}>
              {menuLegal.map((item) =>
                item.href ? (
                  <a
                    key={item.label}
                    className={styles.drawerLink}
                    href={item.href}
                    id={item.id}
                    {...(item.external
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                    onClick={(e) => {
                      if (item.onClick?.(e)) e.preventDefault();
                      setMenuOpen(false);
                    }}
                  >
                    {item.label}
                  </a>
                ) : (
                  <button
                    key={item.label}
                    type="button"
                    className={styles.drawerLink}
                    id={item.id}
                    onClick={(e) => {
                      item.onClick?.(e);
                      setMenuOpen(false);
                    }}
                  >
                    {item.label}
                  </button>
                ),
              )}
            </div>
          </nav>
        </div>
      ) : null}

      {bakeGaps.length > 0 ? (
        <div className={styles.bakeWarn} role="status">
          {extra.shareBakeWarn(manifest.freezeVersion ?? 1, bakeGaps.length)}
        </div>
      ) : null}

      <ShareBreadcrumbBar
        segments={pathSegments}
        canGoBack={stack.length > 1}
        onBack={() => setStack((s) => s.slice(0, -1))}
        onNavigate={(uuid) =>
          setStack(
            visitsFromAncestry(buildShareCanvasStack(manifest, uuid)),
          )
        }
        desktopExtras={narrow ? null : catalogNav}
        ownerSlot={narrow ? null : ownerBlock}
        showInfo={showPathInfo}
        infoSlot={
          narrow ? (
            <>
              {aboutBlock}
              <div className={styles.infoOwnerDivider}>{ownerBlock}</div>
            </>
          ) : (
            aboutBlock
          )
        }
      />

      {chunk ? (
        <div className={styles.canvasHost}>
          <ViewingCanvas
            manifest={manifest}
            items={items}
            canvasParentUuid={parentUuid}
            focusItemUuid={focusItemUuid}
            canvasWidth={chunk.canvasWidth}
            canvasHeight={chunk.canvasHeight}
            backgroundColor={paintColors.canvasBackground}
            viewportBackgroundColor={paintColors.viewportBackground}
            canvasBorderColor={paintColors.canvasBorder}
            itemDefaultBackground={paintColors.palette.itemBackground}
            itemDefaultBorder={paintColors.palette.itemBorder}
            itemDefaultText={paintColors.palette.itemText}
            itemDefaultDropShadow={paintColors.palette.itemDropShadow}
            svgBackgroundUrl={svgBackgroundUrl}
            layoutOverride={layoutOverride}
            onCycleLayout={cycleLayout}
            onEnterContainer={(item) => {
              if (isShareRootItem(manifest, item.uuid)) return;
              setStack((s) => [...s, { canvasUuid: item.uuid }]);
              setSelected(null);
            }}
            onRevealItem={revealPlaceholderOriginal}
            onOpenItem={(item) => {
              if (isShareRootItem(manifest, item.uuid)) return;
              setSelected(item);
            }}
          />
        </div>
      ) : (
        <p className={styles.empty}>{extra.shareNoCanvas}</p>
      )}

      <LegalFooter
        variant="viewer"
        reportHref={reportHref}
        onReportClick={() => {
          if (!currentIdToken()) return false;
          setReportOpen(true);
          return true;
        }}
      />

      {reportOpen ? (
        <ShareReportDialog
          shareId={manifest.shareId}
          slug={manifest.slug}
          title={manifest.title}
          onClose={() => setReportOpen(false)}
        />
      ) : null}

      {selected ? (
        <ItemViewer
          manifest={manifest}
          item={selected}
          onClose={() => setSelected(null)}
          pageUrl={absoluteShareUrl}
        />
      ) : null}
    </div>
  );
}
